<script setup lang="ts">
import { computedAsync } from '@vueuse/core';

import { EditTripStepType } from '@apps/main/new-desktop/modules/editTripModal';
import {
  useBrowserStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';

import { AppButton, SemboLineIllustration } from '@libs/ui';
import { isExperimentAsync } from '@libs/feature-toggle';

import { useTripOverviewModalsStore } from '../../stores/tripOverviewModalsStore';
import { ItinerarySegmentType } from '../../models';

import ItineraryIcon from './ItineraryIcon.vue';

const tripOverviewModalsStore = useTripOverviewModalsStore();
const statsigEventsStore = useStatsigEventsStore();
const browserStore = useBrowserStore();

const isDestinationBannerExperiment = computedAsync<boolean>(
  () => isExperimentAsync('destination_banner'),
  false
);

function openDestinationModal(): void {
  statsigEventsStore.logTripOverviewAddDestinationClicked();
  tripOverviewModalsStore.openEditTripModal({
    stepType: EditTripStepType.AddDestination,
  });
}
</script>

<template>
  <div class="add-destination-section">
    <div v-if="isDestinationBannerExperiment" class="add-destination-banner">
      <div class="add-destination-icon">
        <ItineraryIcon
          class="itinerary-icon"
          :segment-type="ItinerarySegmentType.AddDestination"
        />
      </div>
      <div class="add-destination-text">
        <div class="add-destination-title">
          {{ $t('planner.add_destination_banner.title') }}
        </div>
        <div class="add-destination-description">
          {{ $t('planner.add_destination_banner.content') }}
        </div>
      </div>
      <div class="add-destination-banner-button" @click="openDestinationModal">
        <AppButton
          class="plus-button"
          type="secondary"
          state="enabled"
          size="medium"
          :wide="browserStore.isSmallerThanSmallMin"
        >
          {{ $t('planner.add_destination_banner.button') }}
        </AppButton>
      </div>
      <SemboLineIllustration class="sembo-line-illustration" />
    </div>

    <div v-else class="add-destination-button" @click="openDestinationModal">
      <AppButton
        class="plus-button"
        type="secondary"
        state="enabled"
        size="medium"
        :icon="{ name: 'plus' }"
      >
        {{ $t('planner.add_destination') }}
      </AppButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
.add-destination-section {
  .add-destination-banner {
    --margin-bottom: var(--spacing-32);
    display: flex;
    align-items: center;
    margin-bottom: var(--margin-bottom);
    gap: var(--spacing-16);
    padding: var(--spacing-24) var(--spacing-32);
    background-color: var(--color-white);
    border-top: 1px solid var(--color-scrollbar);
    border-bottom: 1px solid var(--color-scrollbar);
    position: relative;

    @include for-small-devices-max {
      flex-wrap: wrap;
      padding: var(--spacing-24) var(--spacing-16);
      margin-left: var(--spacing-4);
    }

    .add-destination-text {
      z-index: var(--panel-first-level-z-index);
      flex: 1;
      .add-destination-title {
        @include font-link-s;
        color: var(--color-secondary-40);
        margin-bottom: var(--spacing-4);
      }
      .add-destination-description {
        @include font-label-l-light();
        color: var(--color-secondary-40);
        line-height: 14px;
      }
    }

    .add-destination-banner-button {
      position: relative;
      z-index: var(--panel-first-level-z-index);
      @include for-small-devices-max {
        width: 100%;
      }
    }

    .sembo-line-illustration {
      position: absolute;
      right: 140px;
      top: 0;
      bottom: 0;
      margin: auto;
      z-index: var(--notification-z-index);
      @include for-medium-devices-max {
        display: none;
      }
    }
  }

  .add-destination-button {
    width: fit-content;
    display: flex;
    gap: var(--spacing-8);
    cursor: pointer;
    align-items: center;
    padding-left: var(--spacing-24);

    @include for-small-devices-max {
      padding-left: var(--spacing-12);
    }

    .plus-button {
      padding-left: var(--spacing-4);
    }
  }
}
</style>
